import { NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { from, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { PeopleService, Person, PersonSearchResult } from '../../../core/people/people';
import { PersonModal } from '../person-modal/person-modal';
import { BulkAddPeopleModal } from '../bulk-add-people-modal/bulk-add-people-modal';

const PAGE_SIZE = 20;

/** F-005-style as-you-type search against the local catalog only — see
 *  competition-picks.ts, whose add-a-pick search this mirrors. F-029 —
 *  keep these two values in sync with every other celebrity search UI. */
const LIVE_SEARCH_DEBOUNCE_MS = 200;
const LIVE_SEARCH_MIN_LENGTH = 4;

@Component({
  selector: 'app-admin-people-list',
  imports: [ReactiveFormsModule, RouterLink, PersonModal, BulkAddPeopleModal, NgTemplateOutlet],
  templateUrl: './admin-people-list.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './admin-people-list.css',
})
export class AdminPeopleList implements OnInit {
  private readonly people = inject(PeopleService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly items = signal<Person[]>([]);
  readonly total = signal(0);
  readonly page = signal(1);

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.total() / PAGE_SIZE)));

  readonly searchForm = new FormGroup({
    q: new FormControl('', { nonNullable: true }),
  });

  /** Editing an existing catalog row, opened from the list below. */
  readonly openPersonId = signal<number | null>(null);
  /** Adding a new row, opened from a picked add-search result — mutually
   *  exclusive with openPersonId (see modalOpen()/closeModal()). */
  readonly addPrefill = signal<PersonSearchResult | null>(null);
  readonly modalOpen = computed(() => this.openPersonId() !== null || this.addPrefill() !== null);

  /** Search-to-add panel — same local-then-Wikipedia-fallback search the
   *  picks page uses, but picking a result opens the person modal instead
   *  of adding a pick, so the admin can review/edit before it's created. */
  readonly showAddSearch = signal(false);
  readonly addSearchForm = new FormGroup({
    q: new FormControl('', { nonNullable: true }),
  });
  readonly addSearching = signal(false);
  readonly addSearchError = signal<string | null>(null);
  readonly addSearchResults = signal<PersonSearchResult[]>([]);
  readonly addNoResultsFound = signal(false);
  readonly addShowHidden = signal(false);
  /** F-031 — true once the current query's results already reflect a
   *  Wikipedia check (see competition-picks.ts for the full rationale). */
  readonly addWikipediaAlsoSearched = signal(false);

  /** F-027 — bulk add-and-lookup panel, independent of the single add flow
   *  above (both can't meaningfully be open at once, but nothing enforces
   *  that beyond the UI only offering one entry point at a time). */
  readonly bulkAddOpen = signal(false);

  /** Filters non-person Wikipedia noise (a celebrity's movies/discography,
   *  etc.) out of the default results — see competition-picks.ts for why
   *  "no confirmed birth date" reliably means "not a person article" for a
   *  Wikipedia hit; local catalog hits are already-vetted and never hidden. */
  readonly addVisibleResults = computed(() =>
    this.addSearchResults().filter((r) => r.source === 'local' || r.dateOfBirth !== null),
  );
  readonly addHiddenResults = computed(() =>
    this.addSearchResults().filter((r) => r.source === 'wikipedia' && r.dateOfBirth === null),
  );

  constructor() {
    this.wireLiveAddSearch();
  }

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  async search(): Promise<void> {
    this.page.set(1);
    await this.load();
  }

  async goToPage(page: number): Promise<void> {
    if (page < 1 || page > this.totalPages()) return;
    this.page.set(page);
    await this.load();
  }

  open(person: Person): void {
    this.addPrefill.set(null);
    this.openPersonId.set(person.id);
  }

  openAddSearch(): void {
    this.showAddSearch.set(true);
  }

  closeAddSearch(): void {
    this.showAddSearch.set(false);
    this.addSearchForm.controls.q.setValue('');
    this.addSearchResults.set([]);
    this.addSearchError.set(null);
    this.addNoResultsFound.set(false);
    this.addWikipediaAlsoSearched.set(false);
  }

  addResultKey(result: PersonSearchResult): string {
    return result.wikipediaUrl ?? `${result.source}-${result.name}`;
  }

  /** Local catalog hit — open it directly in edit mode (no need to round-trip
   *  through select(), which would just re-resolve the same row). A Wikipedia
   *  candidate opens the modal in add mode, pre-filled, so the admin can
   *  review/edit before it's actually created. */
  selectAddResult(result: PersonSearchResult): void {
    this.closeAddSearch();
    if (result.id !== null) {
      this.openPersonId.set(result.id);
    } else {
      this.openPersonId.set(null);
      this.addPrefill.set(result);
    }
  }

  private wireLiveAddSearch(): void {
    this.addSearchForm.controls.q.valueChanges
      .pipe(
        debounceTime(LIVE_SEARCH_DEBOUNCE_MS),
        map((q) => q.trim()),
        distinctUntilChanged(),
        switchMap((q) => {
          if (q.length < LIVE_SEARCH_MIN_LENGTH) return of<PersonSearchResult[]>([]);
          this.addSearching.set(true);
          this.addSearchError.set(null);
          return from(this.people.search(q, 10, true)).pipe(
            catchError(() => {
              this.addSearchError.set('Search failed — try again in a moment.');
              return of<PersonSearchResult[]>([]);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((results) => {
        this.addSearchResults.set(results);
        this.addShowHidden.set(false);
        this.addNoResultsFound.set(false);
        this.addWikipediaAlsoSearched.set(false);
        this.addSearching.set(false);
      });
  }

  /** Explicit Enter/Search action — full local-then-Wikipedia-fallback search.
   *  F-031 — includeWikipedia (via the "Search Wikipedia anyway" button)
   *  also checks Wikipedia even when the catalog already has hits. */
  async searchToAdd(includeWikipedia = false): Promise<void> {
    const q = this.addSearchForm.getRawValue().q.trim();
    if (!q) return;

    this.addSearching.set(true);
    this.addSearchError.set(null);
    this.addNoResultsFound.set(false);
    try {
      const results = await this.people.search(q, 10, false, false, includeWikipedia);
      this.addSearchResults.set(results);
      this.addShowHidden.set(false);
      this.addNoResultsFound.set(results.length === 0);
      this.addWikipediaAlsoSearched.set(
        includeWikipedia || !results.some((r) => r.source === 'local'),
      );
    } catch {
      this.addSearchError.set('Search failed — try again in a moment.');
    } finally {
      this.addSearching.set(false);
    }
  }

  onModalClosed(): void {
    this.openPersonId.set(null);
    this.addPrefill.set(null);
  }

  openBulkAdd(): void {
    this.bulkAddOpen.set(true);
  }

  closeBulkAdd(): void {
    this.bulkAddOpen.set(false);
  }

  async onModalChanged(): Promise<void> {
    await this.load();
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const q = this.searchForm.getRawValue().q.trim();
      const result = await this.people.list({
        q: q || undefined,
        page: this.page(),
        limit: PAGE_SIZE,
      });
      this.items.set(result.items);
      this.total.set(result.total);
    } catch {
      this.errorMessage.set('Could not load the catalog — try again in a moment.');
    } finally {
      this.loading.set(false);
    }
  }
}
